import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faEnvelope, faPrint, faXmark } from "@fortawesome/pro-regular-svg-icons";
import { useTranslation } from "react-i18next";
import { ButtonNew as Button } from "components/buttonNew";
import { LocaleCurrency } from "helpers/Locale";
import { INewReservationState } from "pages/secure/facility/accommodation/reservations/types";
import { IAccommodationUnitType } from "pages/secure/facility/accommodation/unitTypes/UnitTypes";
import "./confirmReservationModal.scss";

interface IConfirmReservationModalProps {
  isOpen: boolean;
  onClose: () => void;
  newReservationState: INewReservationState;
  accommodationUnitTypes: IAccommodationUnitType[];
  selectedUnitTypes: IAccommodationUnitType[];
}

export const ConfirmReservationModal: React.FC<IConfirmReservationModalProps> = ({
  isOpen,
  onClose,
  newReservationState,
  accommodationUnitTypes,
  selectedUnitTypes,
}) => {
  const { t } = useTranslation();

  if (!isOpen) {
    return null;
  }

  return (
    <div className="reservations-confirmed-overlay" onClick={onClose}>
      <div className="reservations-confirmed-content" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div className="reservations-confirmed-content_success_icon">
            <FontAwesomeIcon icon={faCircleCheck} />
          </div>

          <div>
            <FontAwesomeIcon className="cursor-pointer" icon={faXmark} onClick={onClose} />
          </div>
        </div>

        <div className="mb-3">
          <p className="reservations-confirmed-content_title mt-2">{t("Reservation confirmed")}</p>
          <p className="reservations-confirmed-content_subtitle mt-2">
            {t("Success! Reservation")} #12345 {t("has been successfully booked.")}
          </p>
        </div>

        <div className="reservations-confirmed-content_section_container">
          <p className="reservations-confirmed-content_section_title">{t("Reservation info")}</p>
          <div className="reservations-confirmed-content_section_item_container">
            <p className="reservations-confirmed-content_section_item_title">{t("Check-in")}</p>
            <p className="reservations-confirmed-content_section_item_content">
              {newReservationState?.arrival_date
                ? new Date(newReservationState.arrival_date).toLocaleDateString("en-US", {
                    weekday: "short",
                    month: "short",
                    day: "numeric",
                  })
                : ""}
            </p>
          </div>

          <div className="reservations-confirmed-content_section_item_container">
            <p className="reservations-confirmed-content_section_item_title">{t("Check-out")}</p>
            <p className="reservations-confirmed-content_section_item_content">
              {newReservationState?.departure_date
                ? new Date(newReservationState.departure_date).toLocaleDateString("en-US", {
                    weekday: "short",
                    month: "short",
                    day: "numeric",
                  })
                : ""}
            </p>
          </div>
          <div className="reservations-confirmed-content_section_item_container">
            <p className="reservations-confirmed-content_section_item_title">{t("Duration")}</p>
            <p className="reservations-confirmed-content_section_item_content">
              {newReservationState?.duration
                ? `${newReservationState.duration} night${newReservationState.duration > 1 ? "s" : ""}`
                : "0 nights"}
            </p>
          </div>

          <div className="reservations-confirmed-content_section_item_container">
            <p className="reservations-confirmed-content_section_item_title">{t("Guests")}</p>
            <p className="reservations-confirmed-content_section_item_content">
              {newReservationState?.adults
                ? `${newReservationState.adults} adult${newReservationState.adults > 1 ? "s" : ""}`
                : "0 adults"}
              {newReservationState?.children
                ? `, ${newReservationState.children} child${newReservationState.children > 1 ? "ren" : ""}`
                : ""}
            </p>
          </div>

          <hr />
          <div className="reservations-confirmed-content_section_item_container">
            <p className="reservations-confirmed-content_section_item_title">{t("Unit type")}</p>
            <p className="reservations-confirmed-content_section_item_content">
              {selectedUnitTypes[0] ? accommodationUnitTypes?.find(u => u.id === selectedUnitTypes[0].id)?.title : ""}
            </p>
          </div>
        </div>

        <div className="reservations-confirmed-content_section_container">
          <p className="reservations-confirmed-content_section_title">{t("Price summary")}</p>
          <div className="reservations-confirmed-content_section_item_container">
            <p className="reservations-confirmed-content_section_item_title">{t("Subtotal")}</p>
            <p className="reservations-confirmed-content_section_item_content">
              <LocaleCurrency currency={"cad"} amount={newReservationState?.subtotalPrice} />
            </p>
          </div>

          <div className="reservations-confirmed-content_section_item_container">
            <p className="reservations-confirmed-content_section_item_title">{t("Fees")}</p>
            <p className="reservations-confirmed-content_section_item_content">$0</p>
          </div>
          <div className="reservations-confirmed-content_section_item_container">
            <p className="reservations-confirmed-content_section_item_title">{t("Taxes")}</p>
            <p className="reservations-confirmed-content_section_item_content">
              <LocaleCurrency currency={"cad"} amount={newReservationState?.taxAmount} />
            </p>
          </div>

          <hr />
          <div className="reservations-confirmed-content_section_item_container">
            <p className="reservations-confirmed-content_section_item_total_text">{t("Amount total")}</p>
            <p className="reservations-confirmed-content_section_item_total_amount">
              <LocaleCurrency currency={"cad"} amount={newReservationState?.totalPrice} />
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between mt-4 mb-2 gap-4">
          <Button className="w-full justify-center" type="secondary">
            <FontAwesomeIcon icon={faEnvelope} className="mr-2" />
            {t("Email invoice")}
          </Button>
          <Button className="w-full justify-center" type="secondary">
            <FontAwesomeIcon icon={faPrint} className="mr-2" />
            {t("Print invoice")}
          </Button>
        </div>

        <div>
          <Button onClick={onClose} className="w-full justify-center mt-3 mb-2" type="primary">
            {t("Close")}
          </Button>
        </div>
      </div>
    </div>
  );
};
